import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { ApiResponse, ApiError } from '../types';
import { API_BASE_URL } from '../config/api';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Client-generated correlation ID, echoed back by the backend and stamped on
// every server log line for this request. Kept stable across automatic retries
// (set once per config) so client and server traces line up.
const newCorrelationId = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (!config.headers['X-Correlation-Id']) {
    config.headers['X-Correlation-Id'] = newCorrelationId();
  }
  return config;
});

// Retry GET requests on dropped connections (VPS unreachable / client offline
// mid-request). Only GETs are safe to retry silently — mutations are left to
// the caller/backend idempotency handling.
interface RetryableConfig extends AxiosRequestConfig {
  _retryCount?: number;
}

const MAX_NETWORK_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 800;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const config = error.config as RetryableConfig | undefined;
    const isNetworkError = !error.response && !!error.request;

    if (isNetworkError && config?.method?.toLowerCase() === 'get') {
      config._retryCount = (config._retryCount || 0) + 1;
      if (config._retryCount <= MAX_NETWORK_RETRIES) {
        await sleep(RETRY_BASE_DELAY_MS * config._retryCount);
        return apiClient(config);
      }
    }

    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      // Guard against an infinite reload loop and against hijacking public
      // pages: only redirect when we are NOT already on one of the routes
      // that don't require auth. Without this check, a stale/expired token
      // left in localStorage causes CompanyProvider/TillProvider (which wrap
      // the whole app above the router) to fire a 401 on mount, which would
      // otherwise force-redirect away from /activate or /register before the
      // user ever sees them — e.g. clicking an emailed activation link in a
      // browser that has an old session.
      const publicRoutes = ['/login', '/register', '/activate'];
      if (!publicRoutes.some((route) => window.location.hash.includes(route))) {
        window.location.href = '/#/login';
      }
    }

    // Log full error context for developer investigation
    const correlationId = (error.response?.data as { correlationId?: string })?.correlationId;
    console.error('[API Error]', {
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      status: error.response?.status,
      code: error.response?.data?.error?.code,
      message: error.response?.data?.error?.message,
      correlationId,
      response: error.response?.data,
    });

    return Promise.reject(error);
  }
);

export const apiService = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    apiClient.get<ApiResponse<T>>(url, { params }),

  post: <T>(url: string, data: unknown) =>
    apiClient.post<ApiResponse<T>>(url, data),

  put: <T>(url: string, data: unknown) =>
    apiClient.put<ApiResponse<T>>(url, data),

  patch: <T>(url: string, data: unknown) =>
    apiClient.patch<ApiResponse<T>>(url, data),

  delete: <T>(url: string) =>
    apiClient.delete<ApiResponse<T>>(url),
};

export default apiClient;
